import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  authorshipApi,
  type AuthorshipDecision,
  type AuthorshipThresholds,
  type FlaggedWork,
} from '@/api/authorship';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Input } from '@/components/ui/Input';
import { humanizeError } from '@/lib/errors';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MBlock, MEmpty, MIcon, MLabel, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';

/**
 * CuratorFlaggedWorksPage — работы, к которым есть вопросы.
 *
 * ЭТО НЕ СПИСОК ОБВИНЁННЫХ. Страница показывает факты о том, как работа
 * появилась: вставили её целиком или набирали, сколько это заняло, правили ли
 * черновик. Вывод делает куратор, а не система.
 *
 * Вставка сама по себе ничего не доказывает: ученик мог писать в Word и
 * перенести готовое. Поэтому и формулировки, и кнопки построены так, чтобы
 * нормальным исходом было «всё в порядке», а не «поймали».
 *
 * Ученик этой страницы не видит и о сигналах не знает.
 */
export function CuratorFlaggedWorksPage() {
  const { t } = useTranslation();
  const isAdmin = useAuthStore((st) => st.user?.role) === 'admin';
  const isMobile = useIsMobile();
  const list = useQuery({
    queryKey: ['curator', 'authorship'],
    queryFn: () => authorshipApi.listWorks(),
  });

  const items = list.data?.items ?? [];
  // Неразобранные — сверху: разобранные остаются на странице, чтобы куратор
  // видел, что он уже смотрел, и мог передумать.
  const { pending, decided } = useMemo(
    () => ({
      pending: items.filter((w) => !w.decision),
      decided: items.filter((w) => w.decision),
    }),
    [items],
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.10) ─────────────────────────
  // Строки одним блоком: неразобранные сверху, ниже «Уже разобрано» бледнее. Нажатие раскрывает сигналы, контекст,
  // отрывок и три действия. Пороги сигналов (только админ) — внизу, а не над списком: с телефона их правят реже всего.
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle title={t('flagged.title')} subtitle={t('flagged.subtitle', { count: pending.length })} />
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : items.length === 0 ? (
          <MEmpty icon="task_alt" title={t('flagged.empty')} />
        ) : (
          <>
            {pending.length > 0 && (
              <MBlock>
                {pending.map((w, i) => (
                  <WorkCard key={`${w.kind}-${w.id}`} work={w} asRow last={i === pending.length - 1} />
                ))}
              </MBlock>
            )}
            {decided.length > 0 && (
              <>
                <MLabel>{t('flagged.reviewedHeading')}</MLabel>
                <MBlock>
                  {decided.map((w, i) => (
                    <WorkCard key={`${w.kind}-${w.id}`} work={w} asRow last={i === decided.length - 1} />
                  ))}
                </MBlock>
              </>
            )}
          </>
        )}
        {isAdmin && <ThresholdPanel />}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('flagged.title')}
        subtitle={t('flagged.subtitle', { count: pending.length })}
      />

      {/* Настройка порогов стоит ЗДЕСЬ, а не в отдельном разделе настроек:
          правила двигают, глядя на то, чего они стоили. Рядом — счёт, сколько
          раз каждый сигнал оказался пустым. */}
      {isAdmin && <ThresholdPanel />}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.isError ? (
        <Card variant="subtle" className="text-center text-error">
          {humanizeError(list.error)}
        </Card>
      ) : items.length === 0 ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('flagged.empty')}
        </Card>
      ) : (
        <div className="space-y-6">
          {pending.length > 0 && (
            <ul className="space-y-3">
              {pending.map((w) => (
                <li key={`${w.kind}-${w.id}`}>
                  <WorkCard work={w} />
                </li>
              ))}
            </ul>
          )}

          {decided.length > 0 && (
            <section>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-label text-on-surface-variant">
                {t('flagged.reviewedHeading')}
              </h2>
              <ul className="space-y-3">
                {decided.map((w) => (
                  <li key={`${w.kind}-${w.id}`}>
                    <WorkCard work={w} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </>
  );
}

function WorkCard({ work, asRow = false, last = false }: { work: FlaggedWork; asRow?: boolean; last?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [openNote, setOpenNote] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Телефон: строка свёрнута до нажатия — иначе сигналы и отрывки всех работ шли бы одной стеной.
  const [expanded, setExpanded] = useState(false);

  const decide = useMutation({
    mutationFn: (decision: AuthorshipDecision) =>
      authorshipApi.review(work.kind, work.id, decision, note.trim() || undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['curator', 'authorship'] }),
    onError: (e: unknown) => setErr(humanizeError(e)),
  });

  const flags = work.report?.flags ?? [];
  const context = work.report?.context ?? [];

  if (asRow) {
    const meta = [
      work.student_name || t('flagged.unknownStudent'),
      work.kind === 'essay' ? t('flagged.kindEssay') : t('flagged.kindHomework'),
      typeof work.band === 'number' ? t('flagged.band', { band: work.band }) : '',
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <MRow
        wrap
        dim={!!work.decision}
        onClick={() => setExpanded((v) => !v)}
        leading={<MIcon icon={flags.length ? 'flag' : 'description'} tone={work.decision ? 'muted' : 'warm'} />}
        title={work.title || t('flagged.untitled')}
        meta={meta}
        aside={
          work.decision ? (
            <MTag tone={work.decision === 'ok' ? 'info' : 'warm'}>
              {work.decision === 'ok' ? t('flagged.decidedOk') : t('flagged.decidedTalked')}
            </MTag>
          ) : (
            <span className="material-symbols-outlined flex-none text-[18px] text-on-surface-variant">
              {expanded ? 'expand_less' : 'expand_more'}
            </span>
          )
        }
        below={
          expanded ? (
            <div data-flagged-details className="space-y-2 pb-3">
              {flags.length > 0 && (
                <ul className="space-y-1.5 rounded-2xl bg-warning-container/30 p-3">
                  {flags.map((f) => (
                    <li key={f.code} className="flex gap-2 text-[13px] text-on-surface">
                      <span className="material-symbols-outlined text-base text-warning">flag</span>
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>
              )}
              {context.length > 0 && <p className="text-[12px] text-on-surface-variant">{context.map((f) => f.text).join(' ')}</p>}
              {work.excerpt && (
                // data-selectable: в приложении выделение снято со всей страницы, а фрагмент куратор копирует (13.10.2026).
                <p data-selectable className="line-clamp-6 whitespace-pre-wrap text-[13px] text-on-surface-variant">{work.excerpt}</p>
              )}
              <p className="text-[11.5px] text-on-surface-variant">{new Date(work.created_at).toLocaleString('ru-RU')}</p>
              {openNote && (
                <Input
                  label={t('flagged.noteLabel')}
                  placeholder={t('flagged.notePlaceholder')}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
              {err && <p className="text-xs text-error">{err}</p>}
              <div className="grid grid-cols-2 gap-2">
                <Button variant="ghost" disabled={decide.isPending} onClick={() => decide.mutate('ok')}>
                  {t('flagged.markOk')}
                </Button>
                <Button variant="secondary" disabled={decide.isPending} onClick={() => decide.mutate('talked')}>
                  {t('flagged.markTalked')}
                </Button>
              </div>
              {!openNote && (
                <button type="button" onClick={() => setOpenNote(true)} className="min-h-11 text-[13px] font-semibold text-primary">
                  {t('flagged.addNote')}
                </button>
              )}
            </div>
          ) : undefined
        }
        last={last}
      />
    );
  }

  return (
    <Card className={work.decision ? 'opacity-60' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <CardTitle>{work.title || t('flagged.untitled')}</CardTitle>
          <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-on-surface">
            <span className="material-symbols-outlined text-base text-on-surface-variant">
              person
            </span>
            {work.student_name || t('flagged.unknownStudent')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Pill variant="neutral">
            {work.kind === 'essay' ? t('flagged.kindEssay') : t('flagged.kindHomework')}
          </Pill>
          {typeof work.band === 'number' && (
            <Pill variant="neutral">{t('flagged.band', { band: work.band })}</Pill>
          )}
          {work.decision && (
            <Pill variant={work.decision === 'ok' ? 'success' : 'warning'}>
              {work.decision === 'ok' ? t('flagged.decidedOk') : t('flagged.decidedTalked')}
            </Pill>
          )}
        </div>
      </div>

      {/* Сигналы. Это факты, а не вывод — вывод делает куратор. */}
      {flags.length > 0 && (
        <ul className="mt-3 space-y-1.5 rounded-button bg-warning-container/30 p-3">
          {flags.map((f) => (
            <li key={f.code} className="flex gap-2 text-sm text-on-surface">
              <span className="material-symbols-outlined text-base text-warning">flag</span>
              <span>{f.text}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Картина целиком: сколько писал, сколько правил. Без этого одна строка
          сигнала выглядит приговором. */}
      {context.length > 0 && (
        <p className="mt-2 text-xs text-on-surface-variant">
          {context.map((f) => f.text).join(' ')}
        </p>
      )}

      {work.excerpt && (
        // data-selectable: в приложении выделение снято со всей страницы, а фрагмент куратор копирует (13.10.2026).
        <p data-selectable className="mt-3 whitespace-pre-wrap text-sm text-on-surface-variant line-clamp-4">
          {work.excerpt}
        </p>
      )}

      <p className="mt-2 flex items-center gap-1.5 text-xs text-on-surface-variant">
        <span className="material-symbols-outlined text-sm">schedule</span>
        {new Date(work.created_at).toLocaleString('ru-RU')}
      </p>

      {openNote && (
        <div className="mt-3">
          <Input
            label={t('flagged.noteLabel')}
            placeholder={t('flagged.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      )}

      {err && <p className="mt-2 text-xs text-error">{err}</p>}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-outline-variant pt-3">
        <Button
          variant="ghost"
          disabled={decide.isPending}
          onClick={() => decide.mutate('ok')}
        >
          {t('flagged.markOk')}
        </Button>
        <Button
          variant="secondary"
          disabled={decide.isPending}
          onClick={() => decide.mutate('talked')}
        >
          {t('flagged.markTalked')}
        </Button>
        {!openNote && (
          <button
            onClick={() => setOpenNote(true)}
            className="text-xs font-medium text-primary hover:underline"
          >
            {t('flagged.addNote')}
          </button>
        )}
      </div>
    </Card>
  );
}

/**
 * ThresholdPanel — пороги и их цена, для директора.
 *
 * Правильных чисел здесь не знает никто: значения по умолчанию подобраны
 * щадящими, чтобы сначала не трогать честных. Столбцы «пусто» и «поговорили»
 * показывают, чего каждый сигнал стоил на НАШИХ учениках — вот по ним пороги и
 * двигают, а не на глаз.
 */
function ThresholdPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<AuthorshipThresholds | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['authorship', 'settings'],
    queryFn: authorshipApi.getSettings,
    enabled: open,
  });
  const calibration = useQuery({
    queryKey: ['authorship', 'calibration'],
    queryFn: authorshipApi.getCalibration,
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (payload: AuthorshipThresholds) => authorshipApi.saveSettings(payload),
    onSuccess: (saved) => {
      setDraft(saved);
      qc.invalidateQueries({ queryKey: ['authorship', 'settings'] });
    },
    onError: (e: unknown) => setErr(humanizeError(e)),
  });

  const current = draft ?? settings.data ?? null;

  const FIELDS: { key: keyof AuthorshipThresholds; step?: number }[] = [
    { key: 'min_words' },
    { key: 'paste_share', step: 0.05 },
    { key: 'largest_paste_words' },
    { key: 'max_wpm' },
    { key: 'no_edits_min_words' },
    { key: 'band_gap_profile', step: 0.5 },
    { key: 'band_gap_history', step: 0.5 },
    { key: 'min_past_works' },
  ];

  if (!open) {
    return (
      <Card variant="subtle" className="mb-4">
        <button
          onClick={() => setOpen(true)}
          className="text-sm font-medium text-primary hover:underline"
        >
          {t('flagged.thresholdsOpen')}
        </button>
      </Card>
    );
  }

  return (
    <Card variant="subtle" className="mb-4 space-y-3">
      <CardTitle>{t('flagged.thresholdsTitle')}</CardTitle>
      <p className="text-xs text-on-surface-variant">{t('flagged.thresholdsHint')}</p>

      {settings.isLoading || !current ? (
        <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map(({ key, step }) => (
              <Input
                key={key}
                type="number"
                step={step ?? 1}
                label={t(`flagged.threshold_${key}`)}
                value={current[key]}
                onChange={(e) =>
                  setDraft({ ...current, [key]: Number(e.target.value) || 0 })
                }
              />
            ))}
          </div>

          {calibration.data && calibration.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-label text-on-surface-variant">
                    <th className="py-1 pr-3">{t('flagged.calSignal')}</th>
                    <th className="py-1 pr-3">{t('flagged.calOk')}</th>
                    <th className="py-1">{t('flagged.calTalked')}</th>
                  </tr>
                </thead>
                <tbody>
                  {calibration.data.map((row) => (
                    <tr key={row.code} className="border-t border-outline-variant">
                      <td className="py-1 pr-3">{t(`flagged.signal_${row.code}`, row.code)}</td>
                      <td className="py-1 pr-3">{row.ok}</td>
                      <td className="py-1">{row.talked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {err && <p className="text-xs text-error">{err}</p>}

          <div className="flex gap-2">
            <Button loading={save.isPending} onClick={() => current && save.mutate(current)}>
              {t('common.save')}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.close')}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
